import { PendingSecondaryProQaLaunch } from '@mark43/cad-api';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useToast } from '@cad-arc';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../../../action-broadcaster/use-integrated-action-broadcaster';
import { DISCIPLINE_LABEL_ENUM } from '../constants';
import {
    Discipline,
    generateTempIncidentId,
    isTemporaryIncidentId,
    normalizeDiscipline,
} from '../helpers/pro-qa-buttons-helpers';

type DisciplineSelectOptions = { isSecondarySession?: boolean };
type DisciplineSelectHandler = (
    discipline: Discipline,
    launchIncidentId?: string,
    options?: DisciplineSelectOptions
) => void;

export function useProQaSecondaryLaunchActionHandler(args: {
    enhancementsEnabled?: boolean;
    eventId?: number;
    agencyEventNumber?: string;
    temporaryIncidentIdsRef: MutableRefObject<Record<Discipline, string | undefined>>;
    handleDisciplineSelect: DisciplineSelectHandler;
    consumePendingSecondaryLaunches: (
        agencyEventNumber: string
    ) => Promise<PendingSecondaryProQaLaunch[] | undefined>;
}) {
    const toast = useToast();
    const {
        enhancementsEnabled,
        eventId,
        agencyEventNumber,
        temporaryIncidentIdsRef,
        handleDisciplineSelect,
        consumePendingSecondaryLaunches,
    } = args;
    const previousEventIdRef = useRef<number | undefined>(eventId);
    const processedLaunchKeysRef = useRef<Set<string>>(new Set());
    const lastPendingLaunchFetchKeyRef = useRef<string>();

    const isIdentifierForCurrentPanel = useCallback(
        (identifier?: string | null) => {
            const resolvedIdentifier = identifier?.trim();
            if (!resolvedIdentifier) {
                return false;
            }

            if (agencyEventNumber?.trim() === resolvedIdentifier) {
                return true;
            }

            return Object.values(temporaryIncidentIdsRef.current).some(
                (temporaryIncidentId) => temporaryIncidentId?.trim() === resolvedIdentifier
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [agencyEventNumber]
    );

    useEffect(() => {
        if (previousEventIdRef.current !== eventId) {
            processedLaunchKeysRef.current.clear();
            previousEventIdRef.current = eventId;
            lastPendingLaunchFetchKeyRef.current = undefined;
        }
    }, [eventId]);

    const consumePendingSecondaryLaunchesForEvent = useCallback(
        (targetAgencyEventNumber: string | undefined | null) => {
            if (!enhancementsEnabled) {
                return;
            }
            const resolvedEventNumber = targetAgencyEventNumber?.trim();
            if (!resolvedEventNumber) {
                return;
            }
            if (lastPendingLaunchFetchKeyRef.current === resolvedEventNumber) {
                return;
            }

            lastPendingLaunchFetchKeyRef.current = resolvedEventNumber;
            consumePendingSecondaryLaunches(resolvedEventNumber)
                .then((pendingLaunches) => {
                    const startedDisciplines: Discipline[] = [];
                    pendingLaunches?.forEach(({ targetCadDiscipline, eventNumber }) => {
                        const normalizedDiscipline = normalizeDiscipline(targetCadDiscipline);
                        if (!eventNumber || !normalizedDiscipline) {
                            return;
                        }
                        if (
                            isAlreadyProcessedLaunch(
                                processedLaunchKeysRef.current,
                                normalizedDiscipline,
                                eventNumber
                            )
                        ) {
                            return;
                        }
                        handleDisciplineSelect(normalizedDiscipline, eventNumber, {
                            isSecondarySession: true,
                        });
                        startedDisciplines.push(normalizedDiscipline);
                    });
                    if (startedDisciplines.length > 0) {
                        toast({
                            status: 'default',
                            description: buildSecondaryLaunchToastDescription(startedDisciplines),
                        });
                    }
                })
                .catch(() => {
                    if (lastPendingLaunchFetchKeyRef.current === resolvedEventNumber) {
                        lastPendingLaunchFetchKeyRef.current = undefined;
                    }
                });
        },
        [consumePendingSecondaryLaunches, enhancementsEnabled, handleDisciplineSelect, toast]
    );

    const proQaSecondaryLaunchActionHandler = useCallback<ActionHandler<'EVENT_V2'>>(
        (action) => {
            if (!enhancementsEnabled) {
                return false;
            }
            if (action.type === 'PRO_QA_CONSUME_PENDING_SECONDARY_LAUNCHES') {
                if (!isIdentifierForCurrentPanel(action?.payload?.agencyEventNumber)) {
                    return false;
                }
                consumePendingSecondaryLaunchesForEvent(action?.payload?.agencyEventNumber);
                return true;
            }

            if (action.type !== 'PRO_QA_SECONDARY_LAUNCH' || !eventId) {
                // primary event is a draft and the secondary launch should only happen once the save completes.
                return false;
            }
            if (!isIdentifierForCurrentPanel(action.payload.tempEventId)) {
                return false;
            }
            if (agencyEventNumber?.trim() && isTemporaryIncidentId(action.payload.tempEventId)) {
                // this launch was emitted while the primary event was still a draft
                return true;
            }
            const normalizedTargetDiscipline = normalizeDiscipline(
                action.payload.targetCadDiscipline
            );
            if (!normalizedTargetDiscipline) {
                return false;
            }
            const existingTempId = temporaryIncidentIdsRef.current[normalizedTargetDiscipline];
            const trimmedSavedEventNumber = agencyEventNumber?.trim();
            const incidentIdForLaunch = trimmedSavedEventNumber
                ? trimmedSavedEventNumber
                : existingTempId ?? generateTempIncidentId();

            if (
                isAlreadyProcessedLaunch(
                    processedLaunchKeysRef.current,
                    normalizedTargetDiscipline,
                    incidentIdForLaunch
                )
            ) {
                return false;
            }

            handleDisciplineSelect(normalizedTargetDiscipline, incidentIdForLaunch, {
                isSecondarySession: true,
            });
            toast({
                status: 'default',
                description: buildSecondaryLaunchToastDescription([normalizedTargetDiscipline]),
            });
            return true;
        },
        [
            agencyEventNumber,
            consumePendingSecondaryLaunchesForEvent,
            eventId,
            enhancementsEnabled,
            handleDisciplineSelect,
            isIdentifierForCurrentPanel,
            temporaryIncidentIdsRef,
            toast,
        ]
    );

    useIntegratedActionBroadcaster({
        windowType: 'EVENT_V2',
        handler: proQaSecondaryLaunchActionHandler,
        ignoreActiveWorkspaceWindowType: true,
    });
}

/**
 * De-duplicates secondary launches by tracking `discipline:identifier` keys.
 * Returns true if the launch was already processed (duplicate), false if it is new and should proceed.
 */
function isAlreadyProcessedLaunch(
    processedKeys: Set<string>,
    discipline: Discipline,
    identifier: string | number | null | undefined
): boolean {
    if (identifier === undefined || identifier === null) {
        return false;
    }
    const key = `${discipline}:${String(identifier)}`;
    if (processedKeys.has(key)) {
        return true;
    }
    processedKeys.add(key);
    return false;
}

function buildSecondaryLaunchToastDescription(disciplines: Discipline[]): string {
    const disciplineLabels = disciplines.map(
        (discipline) => DISCIPLINE_LABEL_ENUM[discipline].enhancementLabel
    );

    if (disciplineLabels.length === 1) {
        return `A secondary ${disciplineLabels[0]} session has been initiated in ProQA.`;
    }

    return `Secondary ${joinLabels(disciplineLabels)} sessions have been initiated in ProQA.`;
}

function joinLabels(labels: string[]): string {
    if (labels.length <= 1) {
        return labels[0] ?? '';
    }

    if (labels.length === 2) {
        return `${labels[0]} and ${labels[1]}`;
    }

    return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
}
